import { FeatureFlag, isFeatureEnabled } from 'app/utils/helpers';
import { Button } from 'app/design/primitives/button';
import { SquareLogo } from 'app/design/icons/square-logo';
import { Box, Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';

import { useNavigation } from 'app/hooks/navigation';
import { navigateToNotificationDestination } from 'app/screens/notifications/navigator-helper';
import {
  differenceInCalendarDays,
  isInLast24Hours,
  localeAwareFormat,
  localeAwareFormatDurationShort,
  parseISO,
} from 'app/lib/date';
import type {
  NotificationType,
  GenericNotificationType,
  ReminderNotificationType,
  SuperChangedNotificationType,
} from 'app/types/notification';
import { Placeholder, PlaceholderLine, PlaceholderMedia, ShineOverlay } from 'app/overrides/rn-placeholder';
import { Trans, useTranslation } from 'react-i18next';
import * as Linking from 'expo-linking';
import { ListingThumbnail } from 'app/components/listing-thumbnail';
import type { Category } from 'app/types/category';
import { Pressable, View, type ViewProps } from 'react-native';
import { PCImage } from 'app/design/primitives/image';
import { getImageVariantUrl } from 'app/lib/image-variants';
import { NotificationFavBadge } from 'app/components/notif-fav-badge';
import NotificationSuperPartageur from 'app/design/images/notifSuperPartageur';
import { ListingSubtype, TransactionMode, ListingType } from 'app/types/listing';
import { isDonationListing } from 'app/lib/listing';
import { navigateToLoan } from 'app/screens/root-stack-helper';
import { navigateToChat } from 'app/screens/chat/navigator-helper';
import { navigateToListing, navigateToUserProfile } from 'app/screens/billboard/navigator-helper';
import { LoanStatus } from 'app/types/loan';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconCalendarEvent, IconChevronRight } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { isRequestIcon } from 'app/lib/is-request-icon';
import { RequestImage } from './request-image';
import { SuperBadgeSimple } from 'app/design/icons/badge-super-simple';
import { useUser } from 'app/network/user';
import { canUseOfferHub, navigateToQuickPublishFlow } from 'app/screens/intro/navigator-helper';
import type { User } from 'app/types/user';
const theme = createDSTheme();

const style = {
  fontSize: { title: 15, body: 14, date: 12 },
};

const cardStyle = {
  borderRadius: theme.borderRadii['radius.md'],
  marginHorizontal: theme.spacing['spacing.general.x4'],
  padding: theme.spacing['spacing.general.x4'],
};

function formatNotificationDate(createdAt: string) {
  const date = new Date(createdAt);
  if (isInLast24Hours(date)) {
    return localeAwareFormatDurationShort(date);
  }
  return localeAwareFormat(date, 'd MMM yyyy');
}

function getReminderDate(createdAt: string, endDate?: string, loanEnd?: string) {
  const reminderEndDate = endDate ?? loanEnd;
  return differenceInCalendarDays(parseISO(createdAt), reminderEndDate ? parseISO(reminderEndDate) : new Date());
}

function getReminderTranslationSuffix(isSell: boolean, isService: boolean, isDonation: boolean) {
  if (isSell) return 'Sale';
  if (isService) return 'Service';
  if (isDonation) return 'Donation';
  return '';
}

function getReminderTitle(
  daysToNow: number,
  isSell: boolean,
  translationSuffix: string,
  translate: ReturnType<typeof useTranslation>['t'],
) {
  if (daysToNow > 0) return translate(`lateLoan${translationSuffix}`);
  if (daysToNow === 0) return translate(`onTime${translationSuffix}`);
  return translate(isSell ? 'futureReminderSale' : 'futureReminder', { days: Math.abs(daysToNow) });
}

function navigateFromReminder(
  navigation: ReturnType<typeof useNavigation>,
  loan: ReminderNotificationType['payload']['loan'],
  listingId: string | undefined,
  channelUrl: string,
) {
  if (loan) {
    navigateToLoan(navigation, loan.id, listingId!);
    return;
  }
  navigateToChat(navigation, channelUrl);
}

function UnreadDot() {
  return (
    <View
      style={{
        width: 8,
        height: 8,
        borderRadius: 4,
        backgroundColor: theme.colors['surface.brand.default'],
        marginLeft: theme.spacing['spacing.general.x1'],
        flexShrink: 0,
      }}
    />
  );
}

function isRatingsLink(link?: string) {
  if (typeof link !== 'string') return false;

  const url = parseNotificationUrl(link);
  if (!url) return false;

  return url.pathname === '/main/profile/my-ratings';
}

function parseNotificationUrl(link: string) {
  try {
    return new URL(link, 'https://partage.club');
  } catch {
    return undefined;
  }
}

function isQuickPublishLink(link?: string) {
  return typeof link === 'string' && parseNotificationUrl(link)?.pathname === '/quick-new/choose';
}

function navigateFromNotificationLink(
  link: string,
  navigation: ReturnType<typeof useNavigation>,
  user: User | undefined,
  isUserPending: boolean,
) {
  //detect http schema for external navigation
  if (link.startsWith('http')) {
    if (!parseNotificationUrl(link)) return;
    void Linking.openURL(link);
    return;
  }

  // Example link: /main/profile/my-impact-financial?type=lending
  const url = parseNotificationUrl(link); // dummy base URL is needed because link doesn't contain scheme & hostname
  if (!url) return;

  if (url.pathname === '/quick-new/choose') {
    if (!canUseOfferHub(user?.featureFlags, isUserPending)) return;
    navigateToQuickPublishFlow(navigation, user?.featureFlags, isUserPending);
    return;
  }

  // example URL: /main/profile/my-impact-financial?type=borrowing

  let [, _main, tab, screen] = url.pathname.split('/');
  const params: Record<string, string | string[]> = Object.fromEntries(new URLSearchParams(url.search));

  // Support for array params: paramName[]=value1,value2[,...]
  Object.keys(params).forEach((key) => {
    if (key.endsWith('[]')) {
      const newKey = key.substring(0, key.length - 2);
      params[newKey] = (params[key] as string).split(',');

      delete params[key];
    }
  });

  if (_main !== 'main') {
    // eg. /chat/one-channel?url=...
    screen = tab;
    tab = _main;
  }
  if (!tab) return;
  if (!screen) return;

  if (screen === 'neighbour-profile') {
    const userId = typeof params.userId === 'string' ? params.userId : undefined;
    if (!userId) return;

    navigateToUserProfile(
      navigation,
      userId,
      typeof params.listingId === 'string' ? params.listingId : undefined,
      (typeof params.listingType === 'string' ? params.listingType : undefined) as ListingType | undefined,
      typeof params.channelUrl === 'string' ? params.channelUrl : undefined,
    );
    return;
  }

  // Some in-app listing notifications use the root listing modal instead of a tab route.
  if (tab === 'listingModal' && screen === 'one-listing') {
    const listingId = typeof params.id === 'string' ? params.id : undefined;
    if (!listingId) return;

    navigateToListing(navigation, listingId);
    return;
  }

  navigateToNotificationDestination(navigation, tab, screen, params);
}

function NotificationText({ i18nKey, values }: Readonly<{ i18nKey?: string; values?: Record<string, unknown> }>) {
  if (!i18nKey) return null;

  return (
    <Trans
      components={{
        strong: <Text fontWeight="700" />,
      }}
      defaults={i18nKey}
      i18nKey={i18nKey}
      ns="notifications"
      values={values}
    />
  );
}

function NotificationRating({
  createdAt,
  payload: { title, titleArgs, message, messageArgs, link, picture },
  seen,
}: Readonly<GenericNotificationType>) {
  const { t } = useTranslation('notifications');
  const navigation = useNavigation();
  const { data: user, isPending: isUserPending } = useUser();

  const onPress = () => {
    if (typeof link === 'string') {
      navigateFromNotificationLink(link, navigation, user, isUserPending);
    }
  };

  return (
    <Pressable
      onPress={onPress}
      style={{
        ...cardStyle,
        alignItems: 'center',
        backgroundColor: seen ? theme.colors['surface.default'] : theme.colors['surface.inverse.tertiary-neutral'],
        borderColor: theme.colors['surface.inverse.tertiary-neutral'],
        borderWidth: 1,
        flexDirection: 'row',
        gap: theme.spacing['spacing.general.x3'],
      }}
    >
      {picture ? (
        <PCImage
          aspectRatio={1}
          cachePolicy="memory-disk"
          contentFit={picture.includes('default/media') ? 'contain' : 'cover'}
          height={52}
          recyclingKey={`${picture}-rating-notification`}
          source={{ uri: getImageVariantUrl(picture, 'mini') }}
          style={{ borderRadius: theme.borderRadii['radius.full'], width: 52 }}
        />
      ) : (
        <SquareLogo height={52} width={52} />
      )}

      <Column flex={1} gap="spacing.general.x1">
        <Row alignItems="center">
          <Text color="text.brand" flex={1} fontSize={style.fontSize.title} fontWeight="700" variant="bodyBase">
            <NotificationText i18nKey={title} values={titleArgs} />
          </Text>
          {!seen ? <UnreadDot /> : null}
        </Row>

        <Text flex={1} fontSize={style.fontSize.body} variant="bodyBase">
          <NotificationText i18nKey={message} values={messageArgs} />
        </Text>

        <Row alignItems="center" justifyContent="space-between" marginTop="spacing.general.x1">
          <Text color="text.interactive" fontSize={13} fontWeight="600" variant="bodyBase">
            {t('ratingReceived.cta', { defaultValue: 'View ratings' })}
          </Text>
          <Text color="text.secondary" fontSize={style.fontSize.date} variant="bodyBase">
            {formatNotificationDate(createdAt)}
          </Text>
        </Row>
      </Column>

      <IconChevronRight color={theme.colors['text.tertiary']} size={18} strokeWidth={ICON_STROKE_WIDTH} />
    </Pressable>
  );
}

export function NotificationGeneric({
  createdAt,
  payload: { title, titleArgs, message, messageArgs, link, picture, options },
  seen,
}: Readonly<GenericNotificationType>) {
  const navigation = useNavigation();
  const { data: user, isPending: isUserPending } = useUser();
  const hasOfferHubAccess = canUseOfferHub(user?.featureFlags, isUserPending);

  const onPress = () => {
    // Existing feed items can still contain the previous festival destination.
    const destination = options?.festivalReminder ? '/main/profile/overview' : link;
    if (typeof destination === 'string') {
      navigateFromNotificationLink(destination, navigation, user, isUserPending);
    }
  };

  if (
    !isFeatureEnabled(FeatureFlag.rateAfterLoans) &&
    (['rate.borrower.title', 'rate.lender.title'].includes(title) || isRatingsLink(link))
  )
    return null;
  if (isQuickPublishLink(link) && !hasOfferHubAccess) return null;

  if (options?.festivalReminder) {
    return (
      <Row padding="spacing.general.x4" gap="spacing.general.x3" alignItems="flex-start">
        <Column
          backgroundColor="surface.primary-alt"
          borderRadius="radius.lg"
          width={56}
          height={56}
          alignItems="center"
          justifyContent="center"
        >
          <IconCalendarEvent size={24} color={theme.colors['icon.primary']} strokeWidth={ICON_STROKE_WIDTH} />
        </Column>
        <Column flex={1} gap="spacing.general.x2">
          <Row alignItems="center">
            <Text variant="bodySmallMedium" flex={1}>
              <NotificationText i18nKey={title} values={titleArgs} />
            </Text>
            {!seen ? <UnreadDot /> : null}
          </Row>
          <Text variant="bodySmall">
            <NotificationText i18nKey={message} values={messageArgs} />
          </Text>
          <Button
            variant="primary"
            flexDirection="column"
            maxWidth="100%"
            alignSelf="flex-start"
            paddingVertical="spacing.general.x2"
            paddingHorizontal="spacing.general.x3"
            onPress={onPress}
          >
            {options.ctaLabel}
          </Button>
          <Text variant="bodyXSmall" color="text.secondary">
            {formatNotificationDate(createdAt)}
          </Text>
        </Column>
      </Row>
    );
  }

  if (isRatingsLink(link)) {
    return (
      <NotificationRating
        createdAt={createdAt}
        payload={{ title, titleArgs, message, messageArgs, link, picture }}
        seen={seen}
      />
    );
  }

  const isDefaultRequestIcon = isRequestIcon(picture);

  return (
    <Pressable
      onPress={onPress}
      style={{
        ...cardStyle,
        alignItems: 'flex-start',
        backgroundColor: seen ? theme.colors['surface.default'] : theme.colors['surface.inverse.tertiary-neutral'],
        flex: 1,
        flexDirection: 'row',
        justifyContent: 'flex-start',
        borderWidth: seen ? 0 : 1,
        borderColor: theme.colors['surface.inverse.tertiary-neutral'],
      }}
    >
      <NotificationPicture picture={picture} isDefaultRequestIcon={isDefaultRequestIcon} options={options} />

      <Column flex={1} gap="spacing.general.x1" marginLeft="spacing.general.x3">
        {title ? (
          <Row alignItems="center">
            <Text color="text.brand" flex={1} fontSize={style.fontSize.title} fontWeight="600" variant="bodyBase">
              <NotificationText i18nKey={title} values={titleArgs} />
            </Text>
            {!seen ? <UnreadDot /> : null}
          </Row>
        ) : null}

        {message ? (
          <Text flex={1} fontSize={style.fontSize.body} variant="bodyBase">
            <NotificationText i18nKey={message} values={messageArgs} />
          </Text>
        ) : null}

        <Text color="text.secondary" flex={1} fontSize={style.fontSize.date} variant="bodyBase">
          {formatNotificationDate(createdAt)}
        </Text>
      </Column>
    </Pressable>
  );
}
function NotificationPicture({
  picture,
  isDefaultRequestIcon,
  options,
}: {
  picture: string | undefined;
  isDefaultRequestIcon: boolean;
  options: { favBadge?: { color: string } | undefined } | undefined;
}) {
  return picture ? (
    <View>
      {isDefaultRequestIcon ? (
        <RequestImage width={52} height={52} />
      ) : (
        <PCImage
          aspectRatio={1}
          cachePolicy="memory-disk"
          contentFit={picture.includes('default/media') ? 'contain' : 'cover'}
          height={52}
          recyclingKey={`${picture}-thumbnail`}
          source={{ uri: getImageVariantUrl(picture, 'mini') }}
          style={{ borderRadius: theme.borderRadii['radius.md'] }}
        />
      )}
      {options?.favBadge ? (
        <NotificationFavBadge
          color={theme.colors['icon.error']}
          backgroundColor={theme.colors['surface.tertiary-neutral']}
        />
      ) : null}
    </View>
  ) : (
    <SquareLogo height={52} width={52} />
  );
}

export function NotificationSuperChanged({
  createdAt,
  seen,
  payload: { isInitial, isNowSuper, lostSuper, keptSuper },
}: Readonly<SuperChangedNotificationType>) {
  const { t } = useTranslation();
  const navigation = useNavigation();

  const action = isInitial
    ? isNowSuper
      ? 'initialGained'
      : lostSuper
        ? 'initialLost'
        : 'initialKept'
    : isNowSuper
      ? 'gained'
      : 'lost';

  const onPress = () =>
    navigation.navigate('notification-super-changed', { isNowSuper, lostSuper, keptSuper }, { pop: true });

  return (
    <Pressable onPress={onPress}>
      <View
        style={{
          ...cardStyle,
          alignItems: 'flex-start',
          backgroundColor: seen ? theme.colors['surface.default'] : theme.colors['surface.inverse.tertiary-neutral'],
          flex: 1,
          flexDirection: 'row',
          justifyContent: 'flex-start',
          borderWidth: seen ? 0 : 1,
          borderColor: theme.colors['surface.inverse.tertiary-neutral'],
        }}
      >
        {lostSuper ? (
          <SuperBadgeSimple
            color={theme.colors['icon.disabled']}
            style={{
              alignSelf: 'center',
              backgroundColor: theme.colors['surface.disabled'],
              borderRadius: theme.borderRadii['radius.lg'],
            }}
          />
        ) : (
          <NotificationSuperPartageur />
        )}

        <Column flex={1} gap="spacing.general.x1" marginLeft="spacing.general.x3">
          <Row alignItems="center">
            <Text color="text.brand" flex={1} fontSize={style.fontSize.title} fontWeight="600" variant="bodyBase">
              {t(`superChanged.${action}.title`, { ns: 'notifications' })}
            </Text>
            {!seen ? <UnreadDot /> : null}
          </Row>

          <Text flex={1} fontSize={style.fontSize.body} variant="bodyBase">
            {t(`superChanged.${action}.body`, { ns: 'notifications' })}
          </Text>

          <Text color="text.secondary" flex={1} fontSize={style.fontSize.date} variant="bodyBase">
            {formatNotificationDate(createdAt)}
          </Text>
        </Column>
      </View>
    </Pressable>
  );
}

export function NotificationWelcome({ createdAt, seen }: Readonly<Pick<NotificationType, 'createdAt' | 'seen'>>) {
  const { t } = useTranslation();

  return (
    <View
      style={{
        ...cardStyle,
        alignItems: 'flex-start',
        backgroundColor: seen ? theme.colors['surface.default'] : theme.colors['surface.inverse.tertiary-neutral'],
        flex: 1,
        flexDirection: 'row',
        justifyContent: 'flex-start',
        borderWidth: seen ? 0 : 1,
        borderColor: theme.colors['surface.inverse.tertiary-neutral'],
      }}
    >
      <SquareLogo height={52} width={52} />

      <Column flex={1} gap="spacing.general.x1" marginLeft="spacing.general.x3">
        <Row alignItems="center">
          <Text
            flex={1}
            fontSize={style.fontSize.title}
            fontWeight="600"
            numberOfLines={1}
            color="text.brand"
            variant="bodyBase"
          >
            {t('welcome', { ns: 'notifications' })}
          </Text>
          {!seen ? <UnreadDot /> : null}
        </Row>

        <Text flex={1} fontSize={style.fontSize.body} variant="bodyBase">
          {t('welcomeMessage', { ns: 'notifications' })}
        </Text>

        <Text color="text.secondary" flex={1} fontSize={style.fontSize.date} variant="bodyBase">
          {formatNotificationDate(createdAt)}
        </Text>
      </Column>
    </View>
  );
}

export function NotificationReminder({
  createdAt,
  payload: {
    listingSubtype,
    listingType,
    listingName,
    picture,
    loan,
    listingId,
    listingCategory,
    endDate,
    channelUrl,
    transactionMode,
    isDonation: donationFlag,
  },
  seen,
}: Readonly<ReminderNotificationType>) {
  const { t } = useTranslation('notifications');
  const navigation = useNavigation();

  const daysToNow = getReminderDate(createdAt, endDate, loan?.end);

  const isService = listingSubtype === ListingSubtype.service;
  const isSell = transactionMode === TransactionMode.sell;
  const isDonation = isDonationListing({
    isDonation: donationFlag,
    subtype: listingSubtype ?? ListingSubtype.object,
    transactionMode,
  });
  const tSuffix = getReminderTranslationSuffix(isSell, isService, isDonation);

  const onPress = () => navigateFromReminder(navigation, loan, listingId, channelUrl);

  return (
    <Pressable
      onPress={onPress}
      style={{
        ...cardStyle,
        alignItems: 'flex-start',
        backgroundColor: seen ? theme.colors['surface.default'] : theme.colors['surface.inverse.tertiary-neutral'],
        flex: 1,
        flexDirection: 'row',
        justifyContent: 'flex-start',
        borderWidth: seen ? 0 : 1,
        borderColor: theme.colors['surface.inverse.tertiary-neutral'],
      }}
    >
      <Box>
        <ListingThumbnail
          {...{
            categoryPrimary: listingCategory as Category,
            picture,
            type: listingType,
            subtype: listingSubtype,
            transactionMode,
            isDonation: donationFlag,
            isFound: ([LoanStatus.Accepted, LoanStatus.Completed, LoanStatus.Returned] as LoanStatus[]).includes(
              loan?.status ?? ('' as LoanStatus),
            ),
          }}
          hideCorpoBadge
          size={52}
        />
      </Box>

      <Column flex={1} gap="spacing.general.x1" marginLeft="spacing.general.x3">
        <Row alignItems="center">
          <Text
            color={daysToNow > 0 ? 'surface.error.default' : 'text.primary'}
            flex={1}
            fontSize={style.fontSize.title}
            fontWeight="600"
            numberOfLines={1}
            variant="bodyBase"
          >
            {getReminderTitle(daysToNow, isSell, tSuffix, t)}
          </Text>
          {!seen ? <UnreadDot /> : null}
        </Row>

        <Text flex={1} fontSize={style.fontSize.body} numberOfLines={1} variant="bodyBase">
          {listingName}
        </Text>

        <Text color="text.secondary" flex={1} fontSize={style.fontSize.date} variant="bodyBase">
          {formatNotificationDate(createdAt)}
        </Text>
      </Column>
    </Pressable>
  );
}

export function NotificationSkeleton() {
  const leftElement = (props: ViewProps) => (
    <PlaceholderMedia
      style={[
        {
          height: 52,
          width: 52,
          borderRadius: theme.borderRadii['radius.md'],
          marginEnd: theme.spacing['spacing.general.x3'],
        },
        props.style,
      ]}
    />
  );

  return (
    <Column
      margin="spacing.general.x4"
      padding="spacing.general.x4"
      style={{
        backgroundColor: theme.colors['surface.default'],
        borderRadius: theme.borderRadii['radius.md'],
      }}
    >
      <Placeholder Animation={ShineOverlay} Left={leftElement}>
        <PlaceholderLine style={{ height: 10, width: 200, borderRadius: theme.borderRadii['radius.md'] }} />
        <PlaceholderLine
          style={{
            height: 7.5,
            width: 35,
            borderRadius: theme.borderRadii['radius.md'],
            marginTop: theme.spacing['spacing.general.x1'],
          }}
        />

        <PlaceholderLine style={{ width: 125, height: 10, borderRadius: theme.borderRadii['radius.md'] }} />
      </Placeholder>
    </Column>
  );
}
